/**
 * mappingPlanApply.ts — write a fitted mapping plan into a template slide's XML.
 *
 * `/slides/transform-template` returns a plan, not an assembled slide: for each
 * slot it says what text belongs there and which runs to emphasise. Turning
 * that into OOXML is the client's job (the server never sees the package), and
 * this module is that step.
 *
 * Formatting is inherited, never invented
 * ---------------------------------------
 * A template slot's look — typeface, size, colour, alignment, bullet glyph — is
 * carried by its `<a:pPr>` (paragraph) and `<a:rPr>` (run) properties, not by
 * the text. So a slot's paragraphs are rebuilt from the FIRST paragraph's
 * `<a:pPr>` and the first run's `<a:rPr>` already in the template, with only the
 * text swapped. Emitting bare `<a:r><a:t>…</a:t></a:r>` instead would silently
 * drop the template's design, which is the entire reason the user picked it.
 * `<a:bodyPr>` and `<a:lstStyle>` are preserved verbatim for the same reason.
 *
 * Shapes are addressed by the OOXML id the server reported in `slots`
 * (`<p:cNvPr id="…">`), so nothing here re-derives the role classification that
 * produced the plan keys — see `TemplateSlotRef`.
 *
 * Pure string/XML work: no Office.js, no fflate, so it is directly unit-testable.
 */

import type {
  MappingRichBullet,
  MappingRun,
  PlaceholderAssignment,
  TemplateSlotRef,
} from "./slideTransformTemplateApi";

/** Thrown when the plan cannot be written into the slide at all. */
export class MappingPlanError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MappingPlanError";
  }
}

export interface AppliedSlot {
  readonly slotId: string;
  readonly shapeId: number;
}

export interface MappingPlanApplyResult {
  /** The template slide XML with every resolvable assignment written in. */
  readonly slideXml: string;
  readonly applied: readonly AppliedSlot[];
  /**
   * Slot ids that could not be written, with the reason. Reported rather than
   * thrown: a layout with one unresolvable slot still produces a usable slide,
   * and failing the whole run over it would be worse than a partial fit.
   */
  readonly skipped: readonly { readonly slotId: string; readonly reason: string }[];
}

/* ── XML helpers ────────────────────────────────────────────────── */

/**
 * Escape text for an XML text node or attribute value.
 *
 * Load-bearing: slot text is user content by way of the model, and an
 * unescaped `&` or `<` makes the whole package unopenable — PowerPoint reports
 * only "the file is corrupted", with nothing pointing at the cause.
 */
export function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * The index just past the tag that closes the element opened at `openStart`.
 *
 * Counts nested opens of the same tag so `<p:sp>` inside a `<p:grpSp>` (or a
 * group inside a group) resolves to its own close and not an inner one.
 * Self-closing opens (`<p:sp/>`) are handled by the caller. Returns -1 when the
 * element is unterminated.
 */
function findMatchingClose(xml: string, openStart: number, tag: string): number {
  const openRe = new RegExp(String.raw`<${tag}(?=[\s/>])`, "g");
  const closeTag = `</${tag}>`;
  let depth = 0;
  let cursor = openStart;

  for (;;) {
    openRe.lastIndex = cursor;
    const nextOpen = openRe.exec(xml);
    const nextClose = xml.indexOf(closeTag, cursor);
    if (nextClose === -1) return -1;

    if (nextOpen && nextOpen.index < nextClose) {
      // A self-closing tag opens and closes at once, so it changes no depth.
      const tagEnd = xml.indexOf(">", nextOpen.index);
      if (tagEnd !== -1 && xml[tagEnd - 1] === "/") {
        cursor = tagEnd + 1;
        continue;
      }
      depth += 1;
      cursor = nextOpen.index + 1;
      continue;
    }

    depth -= 1;
    if (depth === 0) return nextClose + closeTag.length;
    cursor = nextClose + closeTag.length;
  }
}

/**
 * Locate the shape carrying `<p:cNvPr id="shapeId">`, as a `[start, end)` range.
 *
 * The id is found first and the enclosing shape resolved outward from it, which
 * is what makes a shape nested inside a group resolve correctly: the nearest
 * preceding opening tag is that shape's own.
 */
export function findShapeRange(
  slideXml: string,
  shapeId: number,
): { readonly start: number; readonly end: number } | null {
  // `id` is matched with a closing quote so 2 cannot match 20.
  const idRe = new RegExp(String.raw`<p:cNvPr\b[^>]*\bid="${shapeId}"`, "g");
  const idMatch = idRe.exec(slideXml);
  if (!idMatch) return null;

  for (const tag of ["p:sp", "p:pic", "p:graphicFrame", "p:cxnSp"]) {
    const openRe = new RegExp(String.raw`<${tag}(?=[\s/>])`, "g");
    let candidate = -1;
    for (;;) {
      const open = openRe.exec(slideXml);
      if (!open || open.index > idMatch.index) break;
      candidate = open.index;
    }
    if (candidate === -1) continue;

    const end = findMatchingClose(slideXml, candidate, tag);
    if (end > idMatch.index) return { start: candidate, end };
  }
  return null;
}

/** Inner XML of the first `<a:pPr>`/`<a:rPr>`-style property element, verbatim. */
function firstElement(xml: string, tag: string): string | null {
  const re = new RegExp(String.raw`<${tag}(?:\s[^>]*)?(?:/>|>[\s\S]*?</${tag}>)`);
  return re.exec(xml)?.[0] ?? null;
}

/* ── Paragraph building ─────────────────────────────────────────── */

/**
 * A run's properties with bold/italic/underline forced to the plan's values.
 *
 * Every flag is written explicitly, including the `false` cases: the template's
 * own `<a:rPr b="1">` (or an inherited bold from the placeholder's list style)
 * would otherwise survive and bold a run the plan wants plain.
 */
function runProperties(rPrTemplate: string | null, run: MappingRun): string {
  const attrs = { b: run.bold ? "1" : "0", i: run.italic ? "1" : "0", u: run.underline ? "sng" : "none" };

  if (!rPrTemplate) {
    return `<a:rPr lang="en-US" b="${attrs.b}" i="${attrs.i}" u="${attrs.u}" dirty="0"/>`;
  }

  // Strip the flags the plan owns, then re-add them, so every other property
  // the template set (font, size, colour, spacing) is carried through untouched.
  //
  // The strip is scoped to rPr's OWN opening tag rather than the whole element:
  // an rPr can contain children with their own attributes (`<a:hlinkClick
  // r:id="…"/>`, `<a:latin typeface="…"/>`), and a blanket replace across the
  // element could reach into one of those.
  const openEnd = rPrTemplate.indexOf(">");
  if (openEnd === -1) return rPrTemplate;

  const selfClosing = rPrTemplate[openEnd - 1] === "/";
  const openTag = rPrTemplate.slice(0, selfClosing ? openEnd - 1 : openEnd);
  const rest = rPrTemplate.slice(openEnd + 1);

  const stripped = openTag
    .replace(/\sb="[^"]*"/g, "")
    .replace(/\si="[^"]*"/g, "")
    .replace(/\su="[^"]*"/g, "");
  const rebuilt = `${stripped} b="${attrs.b}" i="${attrs.i}" u="${attrs.u}"`;

  return selfClosing ? `${rebuilt}/>` : `${rebuilt}>${rest}`;
}

function buildRun(rPrTemplate: string | null, run: MappingRun): string {
  // xml:space="preserve" keeps leading/trailing spaces, which matter when a
  // paragraph is split across emphasised and plain runs ("Revenue" + " grew").
  return `<a:r>${runProperties(rPrTemplate, run)}<a:t xml:space="preserve">${escapeXml(run.text)}</a:t></a:r>`;
}

/** A paragraph whose text carries no emphasis — one run, template properties. */
function buildPlainParagraph(pPr: string, rPrTemplate: string | null, text: string): string {
  if (!text) return `<a:p>${pPr}</a:p>`;
  const rPr = rPrTemplate ?? '<a:rPr lang="en-US" dirty="0"/>';
  return `<a:p>${pPr}<a:r>${rPr}<a:t xml:space="preserve">${escapeXml(text)}</a:t></a:r></a:p>`;
}

function buildRichParagraph(
  pPr: string,
  rPrTemplate: string | null,
  bullet: MappingRichBullet,
): string {
  const runs = (bullet.runs ?? []).filter((run) => run?.text);
  if (runs.length === 0) return `<a:p>${pPr}</a:p>`;
  return `<a:p>${pPr}${runs.map((run) => buildRun(rPrTemplate, run)).join("")}</a:p>`;
}

/**
 * Paragraph text for an assignment, as one string per paragraph.
 *
 * `action: "split"` uses `\n` as the paragraph separator, and the other actions
 * can still contain one, so the split is unconditional. Blank lines are dropped
 * so a trailing newline doesn't leave an empty paragraph behind.
 */
export function assignmentParagraphs(assignment: PlaceholderAssignment): string[] {
  if (assignment.action === "leave_blank") return [];
  const text = assignment.text ?? "";
  if (!text) return [];
  return text.split("\n").map((line) => line.trim()).filter(Boolean);
}

/**
 * Rebuild a `<p:txBody>` around new content, keeping the template's body
 * properties and inheriting paragraph/run styling from what it already held.
 */
export function rebuildTextBody(txBodyXml: string, assignment: PlaceholderAssignment): string {
  const bodyPr = firstElement(txBodyXml, "a:bodyPr") ?? "<a:bodyPr/>";
  const lstStyle = firstElement(txBodyXml, "a:lstStyle") ?? "";
  const pPr = firstElement(txBodyXml, "a:pPr") ?? "";
  const rPrTemplate = firstElement(txBodyXml, "a:rPr");

  const rich = (assignment.richBullets ?? []).filter((b) => (b?.runs ?? []).some((r) => r?.text));
  let paragraphs: string[];

  if (assignment.action === "leave_blank") {
    // A text body must hold at least one paragraph, so an emptied slot keeps a
    // single empty one — the shape stays, with no text in it.
    paragraphs = [`<a:p>${pPr}</a:p>`];
  } else if (rich.length > 0) {
    paragraphs = rich.map((bullet) => buildRichParagraph(pPr, rPrTemplate, bullet));
  } else {
    const lines = assignmentParagraphs(assignment);
    paragraphs =
      lines.length > 0
        ? lines.map((line) => buildPlainParagraph(pPr, rPrTemplate, line))
        : [`<a:p>${pPr}</a:p>`];
  }

  return `<p:txBody>${bodyPr}${lstStyle}${paragraphs.join("")}</p:txBody>`;
}

/* ── Entry point ────────────────────────────────────────────────── */

const TX_BODY_RE = /<p:txBody>[\s\S]*?<\/p:txBody>/;

/**
 * Write `assignments` into `templateSlideXml`, resolving each plan key to its
 * shape through `slots`.
 *
 * Assignments are applied from the LAST shape in the document to the first, so
 * each replacement cannot shift the offsets of one not yet applied.
 *
 * @throws {MappingPlanError} when the slide XML is empty.
 */
export function applyMappingPlan(
  templateSlideXml: string,
  assignments: Readonly<Record<string, PlaceholderAssignment>>,
  slots: readonly TemplateSlotRef[],
): MappingPlanApplyResult {
  if (!templateSlideXml) throw new MappingPlanError("The template slide XML is empty.");

  const shapeIdBySlot = new Map<string, number>();
  for (const slot of slots) {
    if (typeof slot.shapeId === "number") shapeIdBySlot.set(slot.id, slot.shapeId);
  }

  const applied: AppliedSlot[] = [];
  const skipped: { slotId: string; reason: string }[] = [];

  // Resolve every target first, then rewrite in reverse document order.
  const targets: { slotId: string; shapeId: number; start: number; end: number; assignment: PlaceholderAssignment }[] =
    [];

  for (const [slotId, assignment] of Object.entries(assignments)) {
    const shapeId = shapeIdBySlot.get(slotId);
    if (shapeId === undefined) {
      skipped.push({ slotId, reason: "the server did not report which shape this slot is" });
      continue;
    }

    const range = findShapeRange(templateSlideXml, shapeId);
    if (!range) {
      skipped.push({ slotId, reason: `shape ${shapeId} is not in the template slide` });
      continue;
    }

    const shapeXml = templateSlideXml.slice(range.start, range.end);
    if (!TX_BODY_RE.test(shapeXml)) {
      // An image/chart slot has no text body; the plan should not have assigned
      // text to it, so this is reported rather than silently ignored.
      skipped.push({ slotId, reason: `shape ${shapeId} has no text body` });
      continue;
    }

    targets.push({ slotId, shapeId, ...range, assignment });
  }

  targets.sort((a, b) => b.start - a.start);

  let working = templateSlideXml;
  for (const target of targets) {
    const shapeXml = working.slice(target.start, target.end);
    const txBody = TX_BODY_RE.exec(shapeXml)?.[0];
    if (!txBody) {
      skipped.push({ slotId: target.slotId, reason: `shape ${target.shapeId} has no text body` });
      continue;
    }
    const rebuilt = shapeXml.replace(TX_BODY_RE, rebuildTextBody(txBody, target.assignment));
    working = working.slice(0, target.start) + rebuilt + working.slice(target.end);
    applied.push({ slotId: target.slotId, shapeId: target.shapeId });
  }

  // Restore document order. `reverse()` mutates, so it is a statement of its
  // own rather than part of the returned expression: the loop above walks the
  // shapes back-to-front, so that patching one cannot shift the offsets of the
  // shapes it has yet to visit.
  applied.reverse();
  return { slideXml: working, applied, skipped };
}
